import {
  IEntryRecordParametersTranslator,
  IEntryRecordLabels,
  ICustomTypedEntityLabels,
  IGenericRequestParametersTranslator,
  IModificationRecordLabels,
  IModifiedRecordParametersTranslator,
  INamedEntityLabels,
  INamedEntityParametersTranslator,
  IOwnedEntityParametersTranslator,
  IPaginationParametersTranslator,
  IPermissionedEntityLabels,
  IProjectBasedParametersTranslator,
  IRelatedEntityLabels,
  ISoftDeletableFilterParametersTranslator,
  ISoftDeletableLabels,
  IUniqueEntityLabels,
  PropertyTranslator,
  ILockableEntityLabels,
  ILockableEntityParametersTranslator,
  ISignableEntityLabels,
  ITypedEntityParametersTranslator,
  ISignableEntityParametersTranslator,
} from "./GenericTranslator";
import {
  IRelationModel,
  IGenericRequestParameters,
  IEntityMinimalModel,
  IModelWithIntId,
  IOwnedEntity,
  IPaginationParameters,
  IPermissionedEntity,
  IProjectBasedResponseModel,
  IRelatedEntity,
  INamedEntity,
  IRelation,
  ISoftDeletableFilterParameters,
  ISoftDeletable,
  IUniqueEntity,
  IOwnedEntityParameters,
  EntityConstants,
  GenericEntityOrderBy,
  TypedEntitySuggestionsModel,
  ICustomTypedEntity,
  IEntryRecord,
  IModificationRecord,
  ICreatedRecordParameters,
  IModifiedRecordParameters,
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  INamedEntityParameters,
  Nullable,
  IProjectBasedParameters,
  ILockableEntity,
  ILockableEntityParameters,
  ISignableEntity,
  ITypedEntityParameters,
  ISignableEntityParameters,
  TypedEntityOrderBy,
  NamedEntityOrderBy,
  OwnedOrderBy,
} from "./GenericTypes";
import { FingerprintFragment } from "./DataFormats";

export const datasetsConstants: EntityConstants<Dataset, DatasetFilters> = {
  resource: "datasets",
  frontendIndexRoute: "datasets",
  entitySingular: "dataset",
  entityPlural: "datasets",
  icon: "activity",
};

export type DatasetViewableEntityType = "ELN" | "CustomField";
export type DatasetParsingState =
  | "NotYetParsed"
  | "ParsedSuccessfully"
  | "NotParsable"
  | "ParsingFailed"
  | "ParsingDisabled";
export interface Dataset
  extends
    IModelWithIntId,
    IPermissionedEntity,
    IProjectBasedResponseModel,
    IOwnedEntity,
    INamedEntity,
    IRelatedEntity<DatasetRelations>,
    ISoftDeletable,
    IUniqueEntity,
    IEntryRecord,
    IModificationRecord,
    ICustomTypedEntity,
    ILockableEntity,
    ISignableEntity {
  format?: IEntityMinimalModel<string>;
  creationDate: string;
  path: string;
  // sourceBaseDirectory?: string;
  // sourceRelativeDirectory?: string;
  claimed: boolean;
  isViewableEntity: boolean | null;
  viewableEntityType: DatasetViewableEntityType | null;
  autouploadServer?: IEntityMinimalModel;
  bridge?: IEntityMinimalModel;
  parsingState: DatasetParsingState;
  parsedMetadata: {
    parameters: boolean;
    tracks: boolean;
    trackCount: number;
    trackViewerTypes: string[];
  };
  parameters?: Record<string, object>;
  formatVersion?: number;
  parserLogs?: ParserLogModel[];
  tracks?: object;
  datatracks?: MinimalDataTrackModel[];
  tracksHierarchy?: object;
  source: DatasetSource;
  // legacyFields: LegacyFields;
  automaticName?: string;
  isManuallyNamed?: boolean;
}

export interface DatasetSuggestions extends TypedEntitySuggestionsModel<Dataset["id"]> {}

export const DatasetSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...TypedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  ...OwnedOrderBy,
  "CREATION_DATE",
  "PARSING_STATE",
  "FORMAT_ID",
  "SOURCE",
] as const;
export type DatasetSortingOptions = (typeof DatasetSortingOptionsConsts)[number];

export interface DatasetFilters
  extends
    IPaginationParameters,
    IGenericRequestParameters<Dataset, DatasetSortingOptions>,
    ITypedEntityParameters,
    ISoftDeletableFilterParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    IOwnedEntityParameters,
    INamedEntityParameters,
    IProjectBasedParameters,
    ILockableEntityParameters,
    ISignableEntityParameters {
  includeParsedMetadata?: boolean | null;
  includeParsingState?: boolean | null;
  includeDeleted?: boolean;
  creationDateFrom?: string | null;
  creationDateTo?: string | null;
  customFields?: Record<string, number | string | boolean> | null;
  pathContains?: string | null;
  autouploadServer?: number[] | null;
  formatIds?: string[] | null;
  parsingState?: DatasetParsingState[] | null;
  bridgeIds?: number[] | null;
  dataSourceIds?: number[] | null;
  hashes?: string[] | null;
  includeViewableEntities?: boolean | null;
  isClaimed?: boolean | null;
  includeUnclaimed?: boolean | null;
  parameters?: Record<string, number | string | boolean> | null;
  isViewableEntity?: boolean | null;
  customFieldIds?: number[] | null;
  customTypeIds?: number[] | null;
  viewableEntityTypes?: DatasetViewableEntityType[] | null;
  searchTermIncludePaths?: boolean | null;
  searchTermIncludeParameters?: boolean | null;
  hasCustomImport?: boolean | null;
}

export const DatasetFiltersTranslator: PropertyTranslator<DatasetFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,
  ...ITypedEntityParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...ISoftDeletableFilterParametersTranslator,
  ...IOwnedEntityParametersTranslator,
  ...INamedEntityParametersTranslator,
  ...IProjectBasedParametersTranslator,
  ...ILockableEntityParametersTranslator,
  ...ISignableEntityParametersTranslator,
  includeParsedMetadata: "Include parsed metadata",
  includeParsingState: "Include parsing state",
  includeDeleted: "Include trashed",
  creationDateFrom: "Creation date from",
  creationDateTo: "Creation date to",
  customFields: "Custom fields",
  pathContains: "Path contains",
  autouploadServer: "Data source",
  formatIds: "Format IDs",
  parsingState: "Parsing state",
  bridgeIds: "Bridge ID",
  dataSourceIds: "Data source IDs",
  hashes: "Hashes",
  includeViewableEntities: "Include viewable entities",
  isClaimed: "Is claimed",
  includeUnclaimed: "Include unclaimed",
  parameters: "Parameters",
  isViewableEntity: "Is viewable entity",
  viewableEntityTypes: "Viewable entity types",
  searchTermIncludePaths: "paths",
  searchTermIncludeParameters: "parameters",
  hasCustomImport: "Has custom import",
} as const;

export const DatasetSearchTermOptions = {
  searchTermIncludeNames: DatasetFiltersTranslator["searchTermIncludeNames"],
  searchTermIncludeIds: DatasetFiltersTranslator["searchTermIncludeIds"],
  searchTermIncludeUuids: DatasetFiltersTranslator["searchTermIncludeUuids"],
  searchTermIncludePaths: DatasetFiltersTranslator["searchTermIncludePaths"],
  searchTermIncludeParameters: DatasetFiltersTranslator["searchTermIncludeParameters"],
  searchTermIncludeCustomFieldValues: DatasetFiltersTranslator["searchTermIncludeCustomFieldValues"],
} as const;

export const DatasetFieldLabels: PropertyTranslator<Dataset> = {
  ...INamedEntityLabels,
  ...IUniqueEntityLabels,
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IRelatedEntityLabels,
  ...ISoftDeletableLabels,
  ...ICustomTypedEntityLabels,
  ...ILockableEntityLabels,
  ...ISignableEntityLabels,
  creationDate: "Creation date",
  autouploadServer: "Autoupload server",
  bridge: "Bridge",
  claimed: "Claimed",
  datatracks: "Datatracks",
  format: "Data format",
  formatVersion: "Format version",
  id: "Dataset ID",
  isViewableEntity: "Is viewable entity",
  // legacyFields: "Legacy fields",
  owner: "Owned by",
  parameters: "Parameters",
  parserLogs: "Parser logs",
  parsingState: "Parsing state",
  parsedMetadata: "Parsed metadata",
  path: "Path",
  permissions: "Permissions",
  projects: "Projects",
  source: "Data source",
  // sourceBaseDirectory: "Data source base directory",
  // sourceRelativeDirectory: "Data source relative directory",
  tracks: "Tracks",
  tracksHierarchy: "Tracks hierarchy",
  viewableEntityType: "Source type",
  automaticName: "Automatic name",
  isManuallyNamed: "Is manually named",
} as const;

export const AttachmentFieldLabels: PropertyTranslator<Dataset> = {
  ...DatasetFieldLabels,
  id: "Attachment ID",
} as const;

export interface DatasetRelations extends IRelationModel {
  labNotebookEntries: IRelation;
  sharedContent: IRelation;
  projects: IRelation;
}

// Dataset queue
export interface QueueJob {
  Id: number;
  Key: string;
  Priority: number;
  Type: string;
  State: string;
  EnqueueTime: string;
  StartTime: string;
  FinishTime: string;
  DatasetId: number;
  Age: string;
  WaitTime: string;
  RunTime: string;
}

// Dataset files
export type FileStructureVersions = FileStructureVersion[];
export interface FileStructureVersion {
  datasetVersion: number;
  timestamp?: string | null; // ISO date string
  structure: FilesStructure;
}

type VersionedFileState = "NEW" | "MODIFIED";
export interface FilesStructure {
  [name: string]: {
    name?: string;
    contents?: {
      [name: string]: FilesStructure;
    };
    hash?: string;
    size?: number;
    mtime?: number;
    isDirectory?: boolean;
    fileVersion?: number;
    state?: VersionedFileState;
  };
}

export type FileStructureItem = FilesStructure[string];

export interface requestFileBytes {
  path: string;
  size?: number;
  offset?: number;
  fileVersion?: number;
}
export interface requestFileLines {
  path: string;
  count: number;
  line: number;
  maxLineLength: number;
  fileVersion?: number;
}

interface DatasetSource extends IModelWithIntId, INamedEntity {
  type: DatasetSourceType;
}

type DatasetSourceType = "Unknown" | "ManualUpload" | "AutoUpload" | "ForeignServer";

// Dataset Upload
export interface IsDatasetKnown {
  unknownHashes: string[];
}

export interface DatasetFile {
  Name: string;
  Path: string;
  IsPrimary: boolean;
  Mtime: string;
  Size: number;
  Hash: string;
}

export interface DatasetDirectory {
  Name: string;
  Path: string;
  Mtime: string;
}

export interface UploadFileModel {
  id: string;
  fullPath: string;
  isUnique: boolean;
  mtime?: string;
}
export interface DatasetForUploadWriteModel extends Partial<Nullable<Dataset>> {
  filePathsAreAbsolute?: boolean;
  files: UploadFileModel[];
}

export interface FormatFileWriteModel {
  id: string;
  fullPath: string;
  mtime?: string;
  fragments?: FingerprintFragment[];
  hash: string;
}
export interface DatasetForSearch {
  id: string;
  parserId: string;
  checkUpdatable: boolean;
  files: FormatFileWriteModel[];
}
export interface DatasetSearchRequest {
  datasets: DatasetForSearch[];
  onlyViewableEntities?: boolean;
}

export type FormatFileState = "NEW" | "UNCHANGED" | "NEEDSUPDATE" | "DELETE";
export interface FormatFileSearchResult {
  id: string;
  fullPath: string;
  state: FormatFileState;
  size?: number;
  mtime?: number;
}
export interface DatasetSearchResult {
  id: string;
  errors: string[] | null;
  logsId?: number;
  isUpdateable?: boolean;
  files: FormatFileSearchResult[];
}

// Update
export interface DatasetForUpdateWriteModel {
  logsId: number;
  files: UploadFileModel[];
}

// Zip file sizes
export interface ZipFileSize {
  size: number;
  datasetCount: number;
  isDownloadable: boolean;
  maxZipFileSize: number;
  maxDatasetCountPerZipFile: number;
}

// Misc
export interface LegacyFields {
  brukerPulseProgram: string;
  brukerTitle: string;
}

export interface ParserLogModel {
  type: string;
  code: number;
  message: string;
}

export interface MinimalDataTrackModel {
  name: string;
  id: string;
  type: string;
  count: number;
  codec: string;
  size: number[];
  min: number[];
  max: number[];
  numberType: string;
}
